import CustomersService from '../service/CustomersService';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import csvheader from './csvheader.json'
const csvheaderLower = Object.fromEntries(Object.entries(csvheader).map(([k, v]) => [k.toLowerCase(), v]));
import { CustomerTable } from '../components/customers/CustomerTable';
import { CustomerForm } from '../components/customers/CustomerForm';
import { CsvImportDialog } from '../components/customers/CsvImportDialog';
import { DeleteCustomerDialog } from '../components/customers/DeleteCustomerDialog';
import { DeleteCustomersDialog } from '../components/customers/DeleteCustomersDialog';
import { HelpSidebar } from '../components/help/HelpSidebar';
import { ChangelogSidebar } from '../components/changelog/ChangelogSidebar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { 
  validateCustomer, 
  validateCustomerNr, 
  validatePrimaryEmail, 
  validateSecondaryEmail,
  validateCompany,
  validateCompany2,
  validateCountry,
  validateLanguage,
  validateTelephone,
  validateFirstName,
  validateSurName,
  validatePhoneNumber,
  checkDuplicates,
  findDuplicateEmailInPrimaryEmails,
  findDuplicateEmailInSecondaryEmails
} from '../utils/validation';

import 'primeicons/primeicons.css';
import './Customers.css';
import { fetchAuthSession } from 'aws-amplify/auth';

// Canonical single-address email rule — identical to utils/validation and the
// backend computeValidationState. Single address, no comma, TLD >= 2 letters.
const emailRegex = /^[^\s@,]+@[^\s@,]+\.[A-Za-z]{2,}$/;

const emptyFormaValidationState = {
  primaryEmail: "",
  secondaryEmail: "",
  customerNr: "",
  organization: "",
  company: "",
  company2: [],
  country: "",
  language: "",
  telephone: [],
  firstName: "",
  surName: "",
  valid: true
};

const emptyCustomer = {
  customerId: "",
  primaryEmail: "",
  secondaryEmail: "",
  customerNr: "",
  organization: "",
  company: "",
  company2: [],
  country: "",
  language: "",
  telephone: [],
  firstName: "",
  surName: "",
  pkwData: 0,
  lkwData: 0,
  valid: 0,
};

const Customers = () => {
  const customersService = new CustomersService();

  // State
  const [customers, setCustomers] = useState([]);
  const [customerDialog, setCustomerDialog] = useState(false);
  const [deleteCustomerDialog, setDeleteCustomerDialog] = useState(false);
  const [deleteCustomersDialog, setDeleteCustomersDialog] = useState(false);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [selectedCustomers, setSelectedCustomers] = useState(null);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [progressBar, setProgressBar] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressHeader, setProgressHeader] = useState("");
  const [progressBarMax, setProgressBarMax] = useState(0);
  const [currentOrganization, setCurrentOrganization] = useState("");
  const [formValidationState, setFormValidationState] = useState(emptyFormaValidationState);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [helpVisible, setHelpVisible] = useState(false);
  const [changelogVisible, setChangelogVisible] = useState(false);
  
  const toast = useRef(null);
  const dt = useRef(null);

  // Get unique users for changelog filter
  const uniqueUsers = useMemo(() => {
    const users = new Set();
    customers.forEach(c => {
      if (c.updatedBy) users.add(c.updatedBy);
      if (c.owner) users.add(c.owner);
    });
    return Array.from(users);
  }, [customers]);

  // Initialize - Load customers based on user role
  useEffect(() => {
    fetchAuthSession().then((session) => {
      const groups = session.tokens?.idToken?.payload["cognito:groups"];
      const org = session.tokens?.idToken?.payload["custom:organization"];
      setCurrentOrganization(org || "");
      
      if (groups?.includes("GS-USER-ADMIN")) {
        setIsUserAdmin(true);
      }
      
      if (groups?.includes("GS-ADMIN")) {
        setIsAdmin(true);
        loadAllCustomers();
      } else {
        loadCustomers();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dialog handlers
  const openNew = () => {
    const newCustomer = { ...emptyCustomer };
    // Auto-assign organization for non-admins
    if (!isAdmin) {
      newCustomer.organization = currentOrganization;
    }
    setCustomer(newCustomer);
    setFormValidationState(emptyFormaValidationState);
    setCustomerDialog(true);
  };

  const hideDialog = () => {
    setCustomerDialog(false);
  };

  const hideDeleteCustomerDialog = () => {
    setDeleteCustomerDialog(false);
  };

  const hideDeleteCustomersDialog = () => {
    setDeleteCustomersDialog(false);
  };

  // Progress bar handlers
  const showProgressBar = () => {
    setProgress(0);
    setProgressBar(true);
  };

  const hideProgressBar = () => {
    setProgressBar(false);
  };

  // Validation wrapper functions that pass required dependencies
  const handleValidateCustomer = (customer) => {
    return validateCustomer(customer, customers, setFormValidationState);
  };

  const handleValidateCustomerNr = (customerNr) => {
    return validateCustomerNr(customerNr, setFormValidationState);
  };

  const handleValidatePrimaryEmail = (_customer) => {
    return validatePrimaryEmail(_customer, customers, setFormValidationState);
  };

  const handleValidateCompany = (company) => {
    return validateCompany(company, setFormValidationState);
  };

  const handleValidateCountry = (country) => {
    return validateCountry(country, setFormValidationState);
  };

  const handleValidateLanguage = (language) => {
    return validateLanguage(language, setFormValidationState);
  };

  const handleValidateFirstName = (firstName) => {
    return validateFirstName(firstName, setFormValidationState);
  };

  const handleValidateSurName = (surName) => {
    return validateSurName(surName, setFormValidationState);
  };

  const handleValidateOrganization = (organization) => {
    if (!organization || organization.trim() === '') {
      setFormValidationState((state) => ({ 
        ...state, 
        organization: 'Organization is required',
        valid: false 
      }));
      return false;
    }
    setFormValidationState((state) => ({ 
      ...state, 
      organization: '',
      valid: true 
    }));
    return true;
  };

  // CRUD Operations
  // Helper: sanitize secondaryEmail to a clean string (GraphQL schema expects String, not [String])
  const sanitizeSecondaryEmail = (val) => {
    if (Array.isArray(val)) {
      val = val.filter(e => e && e.trim() !== '' && e !== '[]').join(',');
    }
    if (!val || val === '[]' || val === 'null') return '';
    return val;
  };

  async function saveCustomer() {
    if (handleValidateCustomer(customer)) {
      let _customers = [...customers];
      let _customer = { ...customer };

      // Update existing customer
      if (customer.customerId) {
        const updateCustomerInput = {
          primaryEmail: _customer.primaryEmail,
          secondaryEmail: sanitizeSecondaryEmail(_customer.secondaryEmail),
          customerNr: _customer.customerNr,
          company: _customer.company,
          company2: _customer.company2,
          country: _customer.country,
          language: _customer.language,
          telephone: _customer.telephone && _customer.telephone.length > 0 ? _customer.telephone : ['+49000'],
          firstName: _customer.firstName,
          surName: _customer.surName,
          pkwData: _customer.pkwData,
          lkwData: _customer.lkwData
        };
        
        try {
          const result = await customersService.updateCustomer({ 
            customerId: customer.customerId, 
            customer: updateCustomerInput 
          });
          const updatedCustomer = { ...result.data.updateCustomer, valid: true };
          const index = findIndexById(customer.customerId);
          _customers[index] = updatedCustomer;
          setCustomers(_customers);
          setCustomerDialog(false);
          setCustomer(emptyCustomer);
          toast.current.show({ 
            severity: 'success', 
            summary: 'Successful', 
            detail: 'Customer Updated', 
            life: 3000 
          });
        } catch (error) {
          toast.current.show({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Customer Update Failed: ' + (error.message || error), 
            life: 3000 
          });
          // Don't close dialog on error
          return;
        }
      }
      // Create new customer
      else {
        // Auto-assign organization for non-admins
        const organization = isAdmin ? _customer.organization : currentOrganization;
        
        const createCustomerInput = {
          primaryEmail: _customer.primaryEmail,
          secondaryEmail: sanitizeSecondaryEmail(_customer.secondaryEmail),
          customerNr: _customer.customerNr,
          organization: organization, // CRITICAL: Include organization
          company: _customer.company,
          company2: _customer.company2,
          country: _customer.country,
          language: _customer.language,
          telephone: _customer.telephone && _customer.telephone.length > 0 ? _customer.telephone : ['+49000'],
          firstName: _customer.firstName,
          surName: _customer.surName,
          pkwData: _customer.pkwData,
          lkwData: _customer.lkwData
        };
        
        try {
          const result = await customersService.createCustomer({ customer: createCustomerInput });
          const newCustomer = { ...result.data.createCustomer, valid: true };
          // IMPORTANT: Create new array instead of mutating
          const updatedCustomers = [..._customers, newCustomer];
          setCustomers(updatedCustomers);
          setCustomerDialog(false);
          setCustomer(emptyCustomer);
          toast.current.show({ 
            severity: 'success', 
            summary: 'Successful', 
            detail: 'Customer Created', 
            life: 3000 
          });
        } catch (error) {
          toast.current.show({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Customer Creation Failed: ' + (error.message || error), 
            life: 3000 
          });
          // Don't close dialog on error
          return;
        }
      }
    }
  }

  const editCustomer = (customer) => {
    setCustomer({ ...customer });
    handleValidateCustomer(customer);
    setCustomerDialog(true);
  };

  // Inline edit handler - saves changes immediately without opening dialog
  const saveInlineEdit = async (updatedCustomer) => {
    // Skip validation for inline edits - validation happens in the cell editor
    // if (!handleValidateCustomer(updatedCustomer)) {
    //   toast.current.show({ 
    //     severity: 'error', 
    //     summary: 'Validation Error', 
    //     detail: 'Please fix validation errors', 
    //     life: 3000 
    //   });
    //   return;
    // }

    // Ensure secondaryEmail is a clean string (GraphQL expects String, not [String])
    const secondaryEmail = sanitizeSecondaryEmail(updatedCustomer.secondaryEmail);
    
    // Ensure company2 is an array (schema expects [String])
    const company2 = Array.isArray(updatedCustomer.company2)
      ? updatedCustomer.company2
      : (updatedCustomer.company2 ? updatedCustomer.company2.split(',').map(s => s.trim()).filter(Boolean) : []);
    
    // Ensure telephone is an array with default
    const telephone = Array.isArray(updatedCustomer.telephone) && updatedCustomer.telephone.length > 0
      ? updatedCustomer.telephone
      : ['+49000'];
    
    const updateCustomerInput = {
      primaryEmail: updatedCustomer.primaryEmail,
      secondaryEmail: secondaryEmail,
      customerNr: updatedCustomer.customerNr,
      company: updatedCustomer.company,
      company2: company2,
      country: updatedCustomer.country,
      language: updatedCustomer.language,
      telephone: telephone,
      firstName: updatedCustomer.firstName,
      surName: updatedCustomer.surName,
      pkwData: updatedCustomer.pkwData,
      lkwData: updatedCustomer.lkwData,
      validationState: updatedCustomer.validationState
    };
    
    try {
      const result = await customersService.updateCustomer({ 
        customerId: updatedCustomer.customerId, 
        customer: updateCustomerInput 
      });
      
      if (!result.data?.updateCustomer) {
        const errorDetail = result.errors ? result.errors.map(e => e.message).join(', ') : 'No data returned';
        toast.current.show({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Customer Update Failed: ' + errorDetail, 
          life: 5000 
        });
        return;
      }
      
      const savedCustomer = { 
        ...result.data.updateCustomer, 
        valid: result.data.updateCustomer.validationState === 'valid' 
      };
      
      // Update local state
      let _customers = [...customers];
      const index = findIndexById(updatedCustomer.customerId);
      if (index >= 0) {
        _customers[index] = savedCustomer;
        setCustomers(_customers);
      }
      
      toast.current.show({ 
        severity: 'success', 
        summary: 'Successful', 
        detail: 'Customer Updated', 
        life: 3000 
      });
    } catch (error) {
      toast.current.show({ 
        severity: 'error', 
        summary: 'Error', 
        detail: 'Customer Update Failed: ' + (error.message || error), 
        life: 3000 
      });
    }
  };

  const confirmDeleteCustomer = (customer) => {
    setCustomer(customer);
    setDeleteCustomerDialog(true);
  };

  const deleteCustomer = async () => {
    let _customers = customers.filter(val => val.customerId !== customer.customerId);
    try {
      if (isAdmin) {
        await customersService.adminDeleteCustomer({ customerId: customer.customerId });
      } else {
        await customersService.deleteCustomer({ customerId: customer.customerId });
      }
      setCustomers(_customers);
      setDeleteCustomerDialog(false);
      setCustomer(emptyCustomer);
      const detail = "Customer Deleted: " + customer.customerNr;
      toast.current.show({ severity: 'success', summary: 'Successful', detail, life: 3000 });
    } catch (error) {
      console.log(error);
      const detail = "Customer Deletion failed: " + customer.customerNr;
      toast.current.show({ severity: 'error', summary: 'Error', detail, life: 3000 });
    }
  };

  const confirmDeleteSelected = () => {
    setDeleteCustomersDialog(true);
  };

  const deleteSelectedCustomers = async () => {
    let _customers = customers.filter(val => !selectedCustomers.includes(val));
    let _deleteCustomers = customers.filter(val => selectedCustomers.includes(val)).map(val =>
      ({ customerId: val.customerId, owner: val.owner })
    );
    
    try {
      await Promise.all(_deleteCustomers.map(c =>
        isAdmin
          ? customersService.adminDeleteCustomer({ customerId: c.customerId })
          : customersService.deleteCustomer({ customerId: c.customerId })
      ));
      setCustomers(_customers);
      setDeleteCustomersDialog(false);
      setSelectedCustomers(null);
      toast.current.show({ 
        severity: 'success', 
        summary: 'Successful', 
        detail: 'Customers Deleted', 
        life: 3000 
      });
    } catch (error) {
      toast.current.show({ 
        severity: 'error', 
        summary: 'Error', 
        detail: 'Error deleting customer', 
        life: 3000 
      });
    }
  };

  // Helper functions
  const findIndexById = (id) => {
    let index = -1;
    for (let i = 0; i < customers.length; i++) {
      if (customers[i].customerId === id) {
        index = i;
        break;
      }
    }
    return index;
  };

  const getAsArray = (s) => {
    let array = [];
    if (s) {
      array = s.split(',');
    }
    return array;
  };

  // Data loading
  const loadCustomers = () => {
    customersService.listMyCustomers({ limit: 5000, nextToken: null }).then(result => {
      const customers = result.data.listMyCustomers.customers;
      let _customers = [];
      for (var i = 0; i < customers.length; i++) {
        // Create a mutable copy of the customer object (Apollo Client returns frozen objects)
        let _customer = { ...customers[i] };
        
        // Normalize array fields - ensure they're arrays, not null
        // secondaryEmail is now a string, not an array
        _customer.secondaryEmail = _customer.secondaryEmail || "";
        _customer.company2 = _customer.company2 || [];
        _customer.telephone = _customer.telephone || [];
        
        const isValid = handleValidateCustomer(_customer);
        _customer.valid = isValid;
        _customers.push(_customer);
      }
      setCustomers(_customers);
    }).catch(error => {
      console.error('Error loading customers:', error);
    });
  };

  const loadAllCustomers = () => {
    customersService.listAllCustomers({ limit: 5000, nextToken: null }).then(result => {
      const customers = result.data.listAllCustomers.customers;
      let _customers = [];
      for (var i = 0; i < customers.length; i++) {
        // Create a mutable copy of the customer object (Apollo Client returns frozen objects)
        let _customer = { ...customers[i] };
        
        // Normalize array fields - ensure they're arrays, not null
        // secondaryEmail is now a string, not an array
        _customer.secondaryEmail = _customer.secondaryEmail || "";
        _customer.company2 = _customer.company2 || [];
        _customer.telephone = _customer.telephone || [];
        
        const isValid = handleValidateCustomer(_customer);
        _customer.valid = isValid;
        _customers.push(_customer);
      }
      setCustomers(_customers);
    });
  };

  // CSV Import/Export
  /**
   * Pre-checks CSV content for signs of Excel corruption or encoding issues.
   * Returns an object with encoding warnings and data warnings separately.
   */
  const checkCsvQuality = (rawText) => {
    const encodingIssues = [];
    const dataWarnings = [];

    // Note: a UTF-8 BOM is NOT flagged here. It is normal for Excel UTF-8 exports,
    // confirms the file is UTF-8, and is stripped in processCsvImport. Treating it
    // as an encoding problem previously caused valid files to be re-read (and
    // corrupted) as Windows-1252.

    // Encoding issues: garbled umlauts (UTF-8 bytes previously decoded as Windows-1252)
    if (/Ã¤|Ã¶|Ã¼|Ã„|Ã–|Ãœ|Ã\u009f|Â/.test(rawText)) {
      encodingIssues.push('Garbled umlauts detected (ä→Ã¤, ö→Ã¶, etc.) — likely an encoding problem (file is not UTF-8).');
    }

    // Encoding issues: replacement characters (ISO-8859/ANSI read as UTF-8)
    if (/\uFFFD/.test(rawText)) {
      encodingIssues.push('Replacement characters detected (�) — file is likely ANSI/ISO-8859 encoded, not UTF-8.');
    }

    const lines = rawText.split('\n').filter(l => l.trim().length > 0);
    if (lines.length < 2) return { encodingIssues, dataWarnings };

    const header = lines[0];

    // Delimiter: Excel may switch semicolons to commas or tabs
    if (!header.includes(';') && (header.includes(',') || header.includes('\t'))) {
      dataWarnings.push('No semicolons found in header — Excel may have changed the delimiter to commas or tabs.');
    }

    // Sample data rows (up to 20)
    const dataRows = lines.slice(1, 21);
    const allValues = dataRows.map(r => r.split(';')).flat().join(' ');

    // Scientific notation (phone numbers mangled by Excel)
    if (/\d+[\.,]\d+E\+?\d+/i.test(allValues)) {
      dataWarnings.push('Scientific notation detected (e.g. 4.91235E+10) — Excel likely converted phone numbers or large numbers.');
    }

    // Excel boolean localization
    if (/\b(TRUE|FALSE|WAHR|FALSCH)\b/.test(allValues)) {
      dataWarnings.push('Excel-style booleans detected (TRUE/FALSE/WAHR/FALSCH) — expected values are 1/0.');
    }

    // Date-like patterns that shouldn't be in customer data (DD.MM.YYYY, MM/DD/YYYY)
    if (/\b\d{1,2}[\/\.]\d{1,2}[\/\.]\d{2,4}\b/.test(allValues)) {
      dataWarnings.push('Date-like values detected (e.g. 15.04.2026) — Excel may have reformatted numeric fields as dates.');
    }

    return { encodingIssues, dataWarnings };
  };

  /**
   * Reads a file with the given encoding and returns its text content.
   */
  const readFileAs = (file, encoding) => {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = (ev) => resolve(ev.target.result);
      r.onerror = reject;
      r.readAsText(file, encoding);
    });
  };

  /**
   * Core CSV processing logic — parses, validates headers, and imports.
   */
  const processCsvImport = async (csv) => {
    // Strip BOM if present
    const cleanCsv = csv.charCodeAt(0) === 0xFEFF ? csv.slice(1) : csv;
    const data = cleanCsv.split('\n');
    const originalColumnNames = data[0].split(';');
    const cols = data[0].replaceAll(/['"]+/g, '').replaceAll(',,\r', '').replaceAll(' ', '').replaceAll('\r', '').split(';');

    for (var i = 0; i < cols.length; i++) {
      const columnName = csvheader[cols[i]] || csvheaderLower[cols[i].toLowerCase()];
      if (!columnName) {
        hideProgressBar();
        toast.current.show({
          severity: 'warn',
          summary: 'Please fix the CSV header',
          detail: `The column header "${originalColumnNames[i]}" is not recognised. `
            + `Please correct the header row in your CSV file and import it again. `
            + `Accepted column names are: PrimaryEmail, SecondaryEmail, CustomerNr, Organization, `
            + `Company, Company2, Country, Language, Telephone, FirstName, SurName, PkwData, LkwData.`,
          life: 12000
        });
        return false;
      }
      cols[i] = columnName;
    }

    data.shift();
    const filteredData = data.filter((el) => {
      if (el.length === 0) {
        return false;
      }
      const fieldcount = el.split(";").length;
      if (fieldcount !== 13) {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: `Some lines could not be imported! see log for details`,
          life: 6000
        });
        return false;
      }
      return true;
    });

    const importedData = filteredData.map(d => {
      d = d.split(';');

      const processedData = cols.reduce((obj, c, i) => {
        obj[c] = d[i].replaceAll(/['"]+/g, '').replaceAll(',,\r', '').replaceAll('\r', '');
        (c === 'pkwData' || c === 'lkwData') && (obj[c] = Boolean(Number((obj[c]))));
        (c === 'company2' || c === 'telephone') && (obj[c] = getAsArray(obj[c]));
        return obj;
      }, {});

      // Default telephone to ['+49000'] if empty
      if (!processedData.telephone || processedData.telephone.length === 0) {
        processedData.telephone = ['+49000'];
      }

      return processedData;
    });

    // Check for duplicates against existing customers (match on customerNr + organization + company)
    const duplicates = [];
    const newEntries = [];
    for (const entry of importedData) {
      const existing = customers.find(c =>
        c.customerNr === entry.customerNr &&
        c.organization === entry.organization &&
        c.company === entry.company
      );
      if (existing) {
        duplicates.push({ existing, entry });
      } else {
        newEntries.push(entry);
      }
    }

    // If duplicates found, ask user what to do
    let overwrite = false;
    if (duplicates.length > 0) {
      const dupList = duplicates.map(d => `${d.entry.customerNr} / ${d.entry.organization} / ${d.entry.company}`);
      overwrite = await new Promise((resolve) => {
        confirmDialog({
          header: `${duplicates.length} Duplicate Customer${duplicates.length > 1 ? 's' : ''} Found`,
          icon: 'pi pi-exclamation-triangle',
          message: (
            <div>
              <p style={{ marginBottom: '0.75rem' }}>The following customers already exist:</p>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', maxHeight: '200px', overflow: 'auto' }}>
                {dupList.map((d, i) => <li key={i} style={{ marginBottom: '0.25rem' }}>{d}</li>)}
              </ul>
              <p style={{ marginTop: '0.75rem', fontWeight: 600 }}>Overwrite existing entries or skip them?</p>
            </div>
          ),
          acceptLabel: 'Overwrite',
          rejectLabel: 'Skip duplicates',
          acceptClassName: 'p-button-warning',
          accept: () => resolve(true),
          reject: () => resolve(false),
          // Closing via the X must also resolve the promise (defaults to skip),
          // otherwise the await hangs and the CSV upload control stays disabled.
          onHide: () => resolve(false),
        });
      });
    }

    const totalOps = newEntries.length + (overwrite ? duplicates.length : 0);
    if (totalOps === 0) {
      toast.current.show({ severity: 'info', summary: 'Nothing to import', detail: 'All customers already exist and were skipped.', life: 4000 });
      return;
    }

    showProgressBar();
    setProgressBarMax(totalOps);
    let count = 0;

    let invalidCount = 0;

    // Import new entries
    for (const entry of newEntries) {
      count++;
      setProgress(Math.round((count / totalOps) * 100));
      setProgressHeader("importing customer " + count + " / " + totalOps);
      const res = await customersService.createCustomer({ customer: entry });
      if (res?.data?.createCustomer?.validationState === 'invalid') invalidCount++;
    }

    // Overwrite duplicates if chosen
    if (overwrite) {
      for (const { existing, entry } of duplicates) {
        count++;
        setProgress(Math.round((count / totalOps) * 100));
        setProgressHeader("updating customer " + count + " / " + totalOps);
        const res = await customersService.updateCustomer({ customerId: existing.customerId, customer: entry });
        if (res?.data?.updateCustomer?.validationState === 'invalid') invalidCount++;
      }
    }

    hideProgressBar();
    const skipped = overwrite ? 0 : duplicates.length;
    // Summary. Invalid rows are persisted (never rejected) but flagged so the
    // user can spot and fix them before exporting.
    const parts = [`${totalOps} imported`];
    if (skipped > 0) parts.push(`${skipped} skipped (duplicates)`);
    if (invalidCount > 0) parts.push(`${invalidCount} invalid`);
    toast.current.show({
      severity: invalidCount > 0 ? 'warn' : 'success',
      summary: 'Import complete',
      detail: invalidCount > 0
        ? `${parts.join(', ')}. Invalid rows are highlighted in the table and excluded from export — please fix them before exporting (e.g. Telephone must be E.164 like +49123456789, exactly one primary email, no comma in Company).`
        : `${parts.join(', ')}.`,
      life: invalidCount > 0 ? 12000 : 4000,
    });
    isAdmin ? loadAllCustomers() : loadCustomers();
  };

  const importCSV = async (e) => {
    const file = e.files[0];

    try {
      // Read as UTF-8 first. This is the correct path for the vast majority of
      // files, including UTF-8-with-BOM exports from Excel (the BOM is stripped
      // later in processCsvImport — it CONFIRMS the file is UTF-8 and must never
      // trigger a re-encode).
      let csv = await readFileAs(file, 'UTF-8');

      // Detect genuinely bad encoding and re-encode to UTF-8 on the fly:
      // invalid UTF-8 byte sequences surface as U+FFFD replacement characters,
      // which is the reliable signal that the file is actually ANSI / Windows-1252
      // / ISO-8859. Only then do we re-read it as Windows-1252.
      if (/\uFFFD/.test(csv)) {
        csv = await readFileAs(file, 'windows-1252');
        toast.current.show({
          severity: 'info',
          summary: 'Encoding fixed',
          detail: 'File was not valid UTF-8 — automatically re-read as Windows-1252.',
          life: 4000
        });
      }

      // Quality pre-check for remaining (non-encoding) issues such as a changed
      // delimiter or Excel-mangled values. Run it on the final, correctly decoded text.
      const { encodingIssues, dataWarnings } = checkCsvQuality(csv);
      const allWarnings = [...encodingIssues, ...dataWarnings];
      if (allWarnings.length > 0) {
        const proceed = await new Promise((resolve) => {
          confirmDialog({
            header: 'CSV Quality Warning',
            icon: 'pi pi-exclamation-triangle',
            message: (
              <div>
                <p style={{ marginBottom: '0.75rem' }}>The file shows signs of possible corruption:</p>
                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  {allWarnings.map((w, i) => <li key={i} style={{ marginBottom: '0.5rem' }}>{w}</li>)}
                </ul>
                <p style={{ marginTop: '0.75rem', fontWeight: 600 }}>Import anyway?</p>
              </div>
            ),
            acceptLabel: 'Import anyway',
            rejectLabel: 'Abort',
            acceptClassName: 'p-button-warning',
            accept: () => resolve(true),
            reject: () => resolve(false),
            // Closing via the X (top-right) fires onHide but neither accept nor
            // reject — without this the await hangs and the Import CSV control
            // stays disabled until a page reload.
            onHide: () => resolve(false),
          });
        });
        if (!proceed) {
          hideProgressBar();
          return;
        }
      }

      await processCsvImport(csv);
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: `Error in input format! see log for details`,
        life: 6000
      });
    } finally {
      e.options.clear();
      hideProgressBar();
    }
  };

  const exportCSV = () => {
    // Check if any rows are selected
    if (!selectedCustomers || selectedCustomers.length === 0) {
      toast.current.show({ 
        severity: 'warn', 
        summary: 'No Selection', 
        detail: 'Please select customers to export', 
        life: 3000 
      });
      return;
    }

    // Get fresh customer data by matching IDs (to get updated validationState)
    const selectedIds = selectedCustomers.map(c => c.customerId);
    const freshSelectedCustomers = customers.filter(c => selectedIds.includes(c.customerId));
    
    // Check for invalid customers using the backend-authoritative validationState.
    const validCustomers = freshSelectedCustomers.filter(c => c.validationState === 'valid');
    const invalidCount = freshSelectedCustomers.length - validCustomers.length;
    
    // Warn if there are invalid customers
    if (invalidCount > 0) {
      toast.current.show({ 
        severity: 'warn', 
        summary: 'Invalid Rows Excluded', 
        detail: `${invalidCount} invalid customer${invalidCount > 1 ? 's' : ''} will not be exported`, 
        life: 5000 
      });
    }

    // If no valid customers, don't export
    if (validCustomers.length === 0) {
      toast.current.show({ 
        severity: 'error', 
        summary: 'Export Failed', 
        detail: 'All selected customers are invalid. Please fix validation errors first.', 
        life: 5000 
      });
      return;
    }

    const responseStructure = [
      { columnName: 'primaryEmail', displayName: 'PrimaryEmail' },
      { columnName: 'secondaryEmail', displayName: 'SecondaryEmail' },
      { columnName: 'customerNr', displayName: 'CustomerNr' },
      { columnName: 'organization', displayName: 'Organization' },
      { columnName: 'company', displayName: 'Company' },
      { columnName: 'company2', displayName: 'Company2' },
      { columnName: 'country', displayName: 'Country' },
      { columnName: 'language', displayName: 'Language' },
      { columnName: 'telephone', displayName: 'Telephone' },
      { columnName: 'firstName', displayName: 'FirstName' },
      { columnName: 'surName', displayName: 'Surname' },
      { columnName: 'pkwData', displayName: 'PKWData' },
      { columnName: 'lkwData', displayName: 'NKWData' }
    ];

    const csvSeparator = dt.current.props.csvSeparator;
    
    const header = responseStructure.map(field => field.displayName).join(csvSeparator);
    const data = validCustomers.map(row => {
      const rowData = responseStructure.map(field => {
        // Telephone: convert array to comma-separated string
        if (field.columnName === 'telephone') {
          let telephone = row[field.columnName];
          if (Array.isArray(telephone)) {
            telephone = telephone.join(',');
          }
          return `${telephone}`;
        }
        // SecondaryEmail: filter out empty values, "[]", and array representations
        if (field.columnName === 'secondaryEmail') {
          let secondaryEmail = row[field.columnName];
          // Handle array case (shouldn't happen but just in case)
          if (Array.isArray(secondaryEmail)) {
            secondaryEmail = secondaryEmail.filter(e => e && e.trim() !== '').join(',');
          }
          // Convert to string and filter out "[]" and empty strings
          secondaryEmail = String(secondaryEmail || '');
          if (secondaryEmail === '[]' || secondaryEmail.trim() === '' || secondaryEmail === 'undefined' || secondaryEmail === 'null') {
            return '';
          }
          return secondaryEmail;
        }
        // Company2: convert array to comma-separated string
        if (field.columnName === 'company2') {
          let company2 = row[field.columnName];
          if (Array.isArray(company2)) {
            company2 = company2.join(',');
          }
          return company2 || '';
        }
        // Country: enforce UPPERCASE
        if (field.columnName === 'country') {
          return (row[field.columnName] || '').toUpperCase();
        }
        // Language: enforce lowercase
        if (field.columnName === 'language') {
          return (row[field.columnName] || '').toLowerCase();
        }
        // Boolean fields: export as 1 or 0
        if (field.columnName === 'pkwData' || field.columnName === 'lkwData') {
          return row[field.columnName] ? 1 : 0;
        }
        return row[field.columnName] || '';
      }).join(csvSeparator);
      return rowData;
    }).join('\n');
    
    const csvData = `${header}\n${data}`.replace(/\n/g, '\r\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'customers-export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Show success message
    toast.current.show({ 
      severity: 'success', 
      summary: 'Export Successful', 
      detail: `${validCustomers.length} customer${validCustomers.length > 1 ? 's' : ''} exported`, 
      life: 3000 
    });
  };

  const filterRows = () => {
    return dt.current.props.value.filter(row => {
      if (!globalFilter) return true;
      const regex = new RegExp(globalFilter, 'i');
      return Object.values(row).some(val => regex.test(val));
    });
  };

  // Form input handlers
  const onInputChange = (e, name) => {
    const val = (e.target && e.target.value) || '';
    let _customer = { ...customer };
    _customer[`${name}`] = val;
    setCustomer(_customer);
  };

  const onInputBooleanChange = (e, name) => {
    const val = e.checked !== undefined ? e.checked : (e.target && e.target.checked) || false;
    let _customer = { ...customer };
    _customer[`${name}`] = val;
    setCustomer(_customer);
  };

  const onSecondaryEmailAdd = (e, _customer) => {
    let errorMessage = "";
    let errors = [];
    const secondaryEmail = e.value;
    
    // Canonical single-address rule (no comma, TLD >= 2) — see emailRegex above.
    if (!emailRegex.test((secondaryEmail || '').trim())) {
      errorMessage = "Invalid email format: " + secondaryEmail;
      errors.push(errorMessage);
      setFormValidationState((state) => ({ ...state, valid: false }));
    }
    
    // Check for duplicate in primary email
    if (secondaryEmail === _customer.primaryEmail) {
      errorMessage = "Secondary email cannot be the same as primary email: " + secondaryEmail;
      errors.push(errorMessage);
      setFormValidationState((state) => ({ ...state, valid: false }));
    }
    
    if (errors.length > 0) {
      setFormValidationState((state) => ({ 
        ...state, 
        secondaryEmail: [...state.secondaryEmail, errors] 
      }));
    }
  };

  const onSecondaryEmailRemove = (value) => {
    const removed = Array.isArray(value) ? value[0] : value;
    // PrimeReact Chips fires onRemove but NOT onChange here, so the value must
    // be updated explicitly — otherwise the chip cannot be removed.
    setCustomer((prev) => {
      const list = Array.isArray(prev.secondaryEmail)
        ? prev.secondaryEmail
        : (prev.secondaryEmail ? String(prev.secondaryEmail).split(',').map((s) => s.trim()) : []);
      return { ...prev, secondaryEmail: list.filter((e) => e !== removed) };
    });
    const index = formValidationState.secondaryEmail.findIndex(item => item.toString().includes(removed));
    if (index >= 0) {
      let _errors = [...formValidationState.secondaryEmail];
      _errors.splice(index, 1);
      setFormValidationState((state) => ({ ...state, secondaryEmail: _errors }));
    }
  };

  const onCompany2Add = (e) => {
    let errorMessage = "";
    let errors = [];
    const company2 = e.value;
    
    if (company2.length > 50) {
      errorMessage = "Company must have max. 50 characters: " + company2;
      errors.push(errorMessage);
      setFormValidationState((state) => ({ ...state, valid: false }));
    }
    
    if (errors.length > 0) {
      setFormValidationState((state) => ({ 
        ...state, 
        company2: [...state.company2, errors] 
      }));
    }
  };

  const onCompany2Remove = (e) => {
    const removed = e.value;
    setCustomer((prev) => {
      const list = Array.isArray(prev.company2)
        ? prev.company2
        : (prev.company2 ? String(prev.company2).split(',').map((s) => s.trim()) : []);
      return { ...prev, company2: list.filter((c) => c !== removed) };
    });
    const index = formValidationState.company2.findIndex(item => item.toString().includes(removed));
    if (index >= 0) {
      let _errors = [...formValidationState.company2];
      _errors.splice(index, 1);
      setFormValidationState((state) => ({ ...state, company2: _errors }));
    }
  };

  const onTelephoneAdd = (e) => {
    let errorMessage = "";
    let errors = [];
    const telephone = e.value;
    errorMessage = validatePhoneNumber(telephone);
    
    if (errorMessage) {
      errors.push(errorMessage);
      setFormValidationState((state) => ({ ...state, valid: false }));
    }
    
    if (errors.length > 0) {
      setFormValidationState((state) => ({ 
        ...state, 
        telephone: [...state.telephone, errors] 
      }));
    }
  };

  const onTelephoneRemove = (e) => {
    const removed = e.value;
    setCustomer((prev) => {
      const list = Array.isArray(prev.telephone)
        ? prev.telephone
        : (prev.telephone ? [prev.telephone] : []);
      return { ...prev, telephone: list.filter((t) => t !== removed) };
    });
    const index = formValidationState.telephone.findIndex(item => item.toString().includes(removed));
    if (index >= 0) {
      let _errors = [...formValidationState.telephone];
      _errors.splice(index, 1);
      setFormValidationState((state) => ({ ...state, telephone: _errors }));
    }
  };

  return (
    <div className="customer-management">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="card">
        <CustomerTable
          customers={customers}
          selectedCustomers={selectedCustomers}
          onSelectionChange={setSelectedCustomers}
          onEdit={saveInlineEdit}
          onEditForm={editCustomer}
          onDelete={confirmDeleteCustomer}
          globalFilter={globalFilter}
          onGlobalFilterChange={setGlobalFilter}
          currentOrganization={currentOrganization}
          isAdmin={isAdmin}
          dtRef={dt}
          onNew={openNew}
          onDeleteSelected={confirmDeleteSelected}
          onImportCSV={importCSV}
          onExportCSV={exportCSV}
          onHelp={() => setHelpVisible(true)}
          onViewChangelog={() => setChangelogVisible(true)}
        />
      </div>

      <CustomerForm
        visible={customerDialog}
        customer={customer}
        validationState={formValidationState}
        isAdmin={isAdmin}
        onHide={hideDialog}
        onSave={saveCustomer}
        onInputChange={onInputChange}
        onInputBooleanChange={onInputBooleanChange}
        onSecondaryEmailAdd={onSecondaryEmailAdd}
        onSecondaryEmailRemove={onSecondaryEmailRemove}
        onCompany2Add={onCompany2Add}
        onCompany2Remove={onCompany2Remove}
        onTelephoneAdd={onTelephoneAdd}
        onTelephoneRemove={onTelephoneRemove}
        onValidateCustomerNr={handleValidateCustomerNr}
        onValidatePrimaryEmail={handleValidatePrimaryEmail}
        onValidateCompany={handleValidateCompany}
        onValidateCountry={handleValidateCountry}
        onValidateLanguage={handleValidateLanguage}
        onValidateFirstName={handleValidateFirstName}
        onValidateSurName={handleValidateSurName}
        onValidateOrganization={handleValidateOrganization}
      />

      <DeleteCustomerDialog
        visible={deleteCustomerDialog}
        customer={customer}
        onHide={hideDeleteCustomerDialog}
        onConfirm={deleteCustomer}
      />

      <DeleteCustomersDialog
        visible={deleteCustomersDialog}
        onHide={hideDeleteCustomersDialog}
        onConfirm={deleteSelectedCustomers}
        count={selectedCustomers?.length || 0}
      />

      <CsvImportDialog
        visible={progressBar}
        progress={progress}
        progressHeader={progressHeader}
        progressBarMax={progressBarMax}
      />

      <HelpSidebar
        visible={helpVisible}
        onHide={() => setHelpVisible(false)}
        isAdmin={isAdmin}
        isUserAdmin={isUserAdmin}
      />

      {isAdmin && (
        <ChangelogSidebar
          visible={changelogVisible}
          onHide={() => setChangelogVisible(false)}
          customers={customers}
          users={uniqueUsers}
        />
      )}
    </div>
  );

}

export default Customers
